import { CircularProgress, IconButton, Tooltip, Typography } from '@mui/material'
import { apiDelete, apiPost } from '../../generic/Api_Functions'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { TextSnippetOutlined, Check, SystemUpdateAltRounded, CommentOutlined, SchemaOutlined } from '@mui/icons-material'
import { useState } from 'react'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import Document from '../document/Document'
import SortIcon from '@mui/icons-material/Sort'
import AlertUI from '../../generic/AlertUI'
import WorkflowCopyDialog from './WorkflowCopyDialog'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import WorkflowTableOperationUpdate from './WorkflowTableOperationUpdate'
import useGetExtendedColumns from '../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import { withExtendedColumnsAfterDescription } from './workflowSelectionColumns'
import WorkflowLastSaveCommentDialog from './WorkflowLastSaveCommentDialog'
import WorkflowSaveAsTemplateDialog from './WorkflowSaveAsTemplateDialog'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').Workflow} Workflow Workflow object
 * @typedef {import('../../generic/utilities/objectStructures/globalStructures').IdCodeName} IdCodeName IdCodeName object
 */

/**
 *
 * @param {Object} props
 * @param {function(Workflow)} props.onWorkflowSelected
 * @param {Function} props.resetSelectedRow
 * @param {IdCodeName} props.partReference
 * @param {Number} props.currentSelectedeWorkflowId
 * @returns
 */
const WorkflowTable = ({ onWorkflowSelected, resetSelectedRow, partReference, currentSelectedeWorkflowId }) => {
  const { t_ } = useSmartTranslation()
  const [showDocument, setShowDocument] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [workflowDoc, setWorkflowDoc] = useState(null)
  const [selectedWorkflowToCopy, setSelectedWorkflowToCopy] = useState(null)
  const [selectedWorkflowToDefault, setSelectedWorkflowToDefault] = useState(null)
  const [editDefaultWorkflow, setEditDefaultWorkflow] = useState(false)
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false)
  const [workflowCopyName, setWorkflowCopyName] = useState()
  const [workflowCopyDescription, setWorkflowCopyDescription] = useState()
  const [commentWorkflowId, setCommentWorkflowId] = useState(0)
  // Story 199: the row (id, code, name) being saved as a new template, or {} while the dialog is closed.
  const [templateSource, setTemplateSource] = useState({})
  const [AlertElement, showAlert] = AlertUI()
  const [dialogAlertElement, showAlertDialog] = AlertUI()
  const frontEndConfig = useFrontEndConfig()[0]
  const isUserAuthorized = useAuthorization()
  // Story 188 (RN1-RN4): the active extended fields of the workflow are columns of this table.
  const extendedColumns = useGetExtendedColumns('workflow')

  // The table mounts ONCE, with its final columns: remounting it costs a request per mount.
  if (extendedColumns === undefined) {
    return <CircularProgress />
  }

  // S122 (HU-07/CA7.1, RN-07.2/D4): the row of a workflow whose linked library BOM has a newer
  // version carries the indicator. The flag comes in the row itself (`bomUpdateAvailable`,
  // contrato-sun.md §2); resolving it per row would be an N+1 over a paginated listing (impacto R7).
  // It is not a search or sort column, hence `search: false`. Story 188 (RN22): it is computed when
  // the response is built, so it must not travel to Sun as a search or filter column either.
  const bomUpdateColumn = {
    name: t_('BOM update'),
    field: 'bomUpdateAvailable',
    search: false,
    disableGenericSearch: true,
    disableFilter: true,
    readOnly: true,
    render: (row) => (
      row?.bomUpdateAvailable === true
        ? (
          <Tooltip title={t_('A newer version of the linked library BOM is available')}>
            <SystemUpdateAltRounded color='info' data-testid='workflow-bom-update-indicator' />
          </Tooltip>
          )
        : null
    )
  }

  // Story 224 (S115, CU6): the row of the LAST version of a workflow with a newer template version available carries the indicator.
  // The flag comes in the row itself (`templateUpdateAvailable`, contrato-sun.md §9.2): an absent field reads as false.
  const templateUpdateColumn = {
    name: t_('Template update'),
    field: 'templateUpdateAvailable',
    search: false,
    disableGenericSearch: true,
    disableFilter: true,
    sortable: false,
    readOnly: true,
    render: (row) => (
      row?.templateUpdateAvailable === true
        ? (
          <Tooltip title={t_('A newer version of the template is available')}>
            <SystemUpdateAltRounded color='info' data-testid='workflow-template-update-indicator' />
          </Tooltip>
          )
        : null
    )
  }

  // Historia 130 (S86, CA5.1 / RN-05.3): el mismo trato que la columna de arriba para la biblioteca
  // de operaciones. La bandera viaja en la fila y solo la puebla `POST getWorkflows`, en la ÚLTIMA
  // versión del workflow; no es columna de búsqueda ni de orden.
  const operationUpdateColumn = {
    name: t_('Update available'),
    field: 'operationUpdateAvailable',
    search: false,
    disableGenericSearch: true,
    disableFilter: true,
    readOnly: true,
    render: (row) => <WorkflowTableOperationUpdate row={row} />
  }

  // Story 188 (RN22): `user` and `locked` are filled when the response is built, not stored in the
  // workflow table, so a search over them makes Sun fail. `default` is NOT excluded: it is what makes
  // Sun filter in memory for the part reference variant.
  const columnsPartReference = [
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Name'),
      field: 'name'
    },
    {
      name: t_('Version'),
      field: 'version'
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Default workflow'),
      type: 'boolean',
      field: 'default'
    },
    {
      name: t_('Create date'),
      field: 'createDate',
      type: 'dateTimePicker',
      readOnly: true
    },
    {
      name: t_('Create node'),
      field: 'createCluster',
      readOnly: true
    },
    {
      name: t_('Last update date'),
      field: 'updateDate',
      type: 'dateTimePicker',
      readOnly: true
    },
    {
      name: t_('Last update node'),
      field: 'updateCluster',
      readOnly: true
    },
    {
      name: t_('User'),
      field: 'user',
      readOnly: true,
      disableGenericSearch: true,
      disableFilter: true
    },
    {
      name: t_('Locked'),
      field: 'locked',
      type: 'boolean',
      disableGenericSearch: true,
      disableFilter: true
    },
    bomUpdateColumn,
    templateUpdateColumn,
    operationUpdateColumn
  ]

  const columnsTemplate = [
    {
      name: t_('Code'),
      field: 'code'
    },
    {
      name: t_('Name'),
      field: 'name'
    },
    {
      name: t_('Version'),
      field: 'version'
    },
    {
      name: t_('Description'),
      field: 'description'
    },
    {
      name: t_('Create date'),
      field: 'createDate',
      type: 'dateTimePicker',
      readOnly: true
    },
    {
      name: t_('Last update date'),
      field: 'updateDate',
      type: 'dateTimePicker',
      readOnly: true
    },
    {
      name: t_('User'),
      field: 'user',
      readOnly: true,
      disableGenericSearch: true,
      disableFilter: true
    },
    {
      name: t_('Locked'),
      field: 'locked',
      type: 'boolean',
      disableGenericSearch: true,
      disableFilter: true
    },
    bomUpdateColumn,
    templateUpdateColumn,
    operationUpdateColumn
  ]

  // Same columns for the main table and the versions accordion (RN3b, RN11).
  const columns = withExtendedColumnsAfterDescription(partReference?.id ? columnsPartReference : columnsTemplate, extendedColumns)

  const dataFetchVersions = async (id, pagination) => {
    let result
    if (partReference?.id) {
      result = await apiPost('workflows/' + id + '/versions/' + partReference.id, pagination)
    } else {
      result = await apiPost('workflows/' + id + '/versions', pagination)
    }
    if (result.content) {
      result.content = result.content.map(element => ({
        ...element,
        moreVersions: false
      }))
    }
    return result
  }
  const actions = {
    delete: {
      fetchFunction: (rows) => {
        onWorkflowSelected(null)
        if (rows.length === 1) {
          if (rows[0].id === currentSelectedeWorkflowId) {
            return [{ resourceId: currentSelectedeWorkflowId, dependencies: ['Can not delete selected workflow. Deselected before deleting it'] }]
          }
          if (rows[0].default) {
            return [{ resourceId: rows[0].id, dependencies: ['Cannot delete the default workflow.'] }]
          }
          return apiDelete('workflows/' + rows[0].id, rows)
        }
      },
      disabled: !isUserAuthorized('DELETE_WORKFLOW')
    },
    resync: {
      resource: 'WORKFLOW',
      // TODO ADD PERMISSIONS WHEN NEW WORKFLOW PERMISSION STRUCTURE IS CREATED
      // disabled: !isUserAuthorized('WORKFLOW_RESYNC'),
      visibilityButton: (row) => { return frontEndConfig?.inCluster && (partReference !== null && partReference !== undefined) }
    },
    accordion: {
      visibilityButton: (row) => {
        return row.moreVersions
      },
      render: (row) => {
        if (!row.moreVersions) {
          return null
        }

        // No tune key here (RN11): the accordion always shows every column and keeps its 3 rows.
        return (
          <SmartMateriaUITable
            actions={actions}
            columns={columns}
            sticky={false}
            dataFetch={(pagination) => dataFetchVersions(row.versionOf, pagination)}
            toolbarDisplay='none'
            dense
            onRowClick={(row) => onWorkflowSelected(row)}
            resetSelectedRow={resetSelectedRow}
            tablePageSize={3}
            pageable
            safeDeleteShowProperty='code'
            sortOptions={{
              default: {
                field: 'version',
                direction: 1
              }
            }}
          />
        )
      }
    },
    custom: [
      {
        name: 'Documents',
        render: (row) => {
          return (
            <IconButton>
              <TextSnippetOutlined />
            </IconButton>
          )
        },
        onClick: (row) => {
          // RN01.6/RNT5: the workflow identifier shown alongside the documents is the code
          const data = { id: row.id, code: row.code, name: row.code + ' || ' + t_('Version') + '  ' + row.version }
          setWorkflowDoc(data)
          setShowDocument(true)
        },
        disabled: !isUserAuthorized('DOCUMENTS_WORKFLOW')
      },
      {
        // Story 188 (RN20): right after Documents, no permission of its own (GATE 1, DG-4).
        name: t_('Show comment'),
        render: () => {
          return (
            <IconButton>
              <CommentOutlined />
            </IconButton>
          )
        },
        onClick: (row) => setCommentWorkflowId(row.id)
      },
      {
        name: t_('Save as'),
        render: (row) => {
          return (
            <IconButton>
              <ContentCopyIcon />
            </IconButton>
          )
        },
        onClick: (row) => {
          setSelectedWorkflowToCopy(row.id)
          setOpenConfirmationDialog(true)
        },
        disabled: !isUserAuthorized('SAVE_AS_WORKFLOW')
      },
      ...(partReference?.id
        ? [
            {
              // Story 199 (POL1): only for part reference workflows, right after Save as, with the WORKFLOWS
              // permission (PO decision: not SAVE_AS_WORKFLOW).
              name: t_('Save as template'),
              render: () => {
                return (
                  <IconButton>
                    <SchemaOutlined />
                  </IconButton>
                )
              },
              onClick: (row) => setTemplateSource({ id: row.id, code: row.code, name: row.name }),
              disabled: !isUserAuthorized('WORKFLOWS')
            },
            {
              name: t_('Set default'),
              render: () => {
                return (
                  <IconButton>
                    <Check />
                  </IconButton>
                )
              },
              onClick: (row) => {
                setSelectedWorkflowToDefault(row.id)
                setEditDefaultWorkflow(true)
              },
              disabled: (row) => !isUserAuthorized('SET_DEFAULT_WORKFLOW') || row.indexValue?.name !== 'APPROVED'
            }
          ]
        : [])
    ]
  }

  const fetchData = async (pagination) => {
    if (partReference) {
      return await apiPost('getWorkflowVersions/' + partReference.id, pagination)
    }
    return await apiPost('getWorkflowVersions', pagination)
  }

  const changeDefaultWorkflow = async () => {
    await apiPost('modifyDefaultWorkflow/' + selectedWorkflowToDefault + '/' + partReference.id).then(() => {
      setEditDefaultWorkflow(false)
      showAlert({ severity: 'success', title: t_('Successfully changed default workflow') })
    }).catch((error) => {
      showAlert({ title: t_('Error updating rule'), message: error.message, severity: error.name })
      setEditDefaultWorkflow(false)
    })
  }

  const generateNewCopyOfWorkflow = async () => {
    const param = {
      name: workflowCopyName,
      description: workflowCopyDescription,
      workflowTemplateId: selectedWorkflowToCopy
    }
    await apiPost('workflow/copy', param).then(() => {
      setOpenConfirmationDialog(false)
      showAlert({ severity: 'success', title: t_('Successfully created') })
    }).catch((error) => {
      showAlertDialog({
        title: '',
        message: error.message,
        severity: 'error'
      })
    })
  }

  return (
    <div>
      {AlertElement}
      <WorkflowLastSaveCommentDialog workflowId={commentWorkflowId} onClose={() => setCommentWorkflowId(0)} />
      <WorkflowSaveAsTemplateDialog
        open={Boolean(templateSource.id)}
        workflowId={templateSource.id}
        defaultCode={templateSource.code}
        defaultName={templateSource.name}
        onClose={() => setTemplateSource({})}
        onSaved={() => showAlert({ severity: 'success', title: t_('Workflow saved as template') })}
      />
      <SmartMateriaUITable
        tableTuneUniqueKey={partReference?.id ? 'workflowSelectionPartReference' : 'workflowSelectionTemplate'}
        actions={actions}
        columns={columns}
        sticky={false}
        dataFetch={fetchData}
        onRowClick={(row) => onWorkflowSelected(row)}
        resetSelectedRow={resetSelectedRow}
        pageable
        sortOptions={{
          default: {
            field: 'code',
            direction: 1
          }
        }}
        safeDeleteShowProperty='code'
      />
      <SmartDialog
        closeCallback={() => setShowDocument(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={<Document reference={workflowDoc} resourceType='WORKFLOW' isDownloadingFile={isDownloadingFile} setIsDownloadingFile={setIsDownloadingFile} />}
        setOpen={showDocument}
      />
      <SmartDialog
        title={{ icon: <SortIcon fontSize='large' />, render: <Typography variant='h5'>{t_('New workflow')}</Typography> }}
        message={<Typography sx={{ marginTop: '25px' }}>{t_('Introduce name and description for the new workflow')}</Typography>}
        setOpen={openConfirmationDialog}
        renderComponent={
          <WorkflowCopyDialog
            AlertElement={dialogAlertElement}
            setAlertElement={showAlertDialog}
            workflowCopyName={workflowCopyName}
            workflowCopyDescription={workflowCopyDescription}
            setWorkflowCopyName={setWorkflowCopyName}
            setWorkflowCopyDescription={setWorkflowCopyDescription}
          />
        }
        close={false}
        cancel
        cancelCallback={() => {
          setOpenConfirmationDialog(false)
        }}
        acceptCallback={() => {
          generateNewCopyOfWorkflow()
        }}
      />
      <SmartDialog
        title={createDialogTitle({ title: t_('Change default workflow'), icon: Check })}
        message={<Typography sx={{ marginTop: '25px' }}>{t_('Are you sure you want to change the default workflow?')}</Typography>}
        setOpen={editDefaultWorkflow}
        close={false}
        cancel
        cancelCallback={() => {
          setEditDefaultWorkflow(false)
        }}
        acceptCallback={changeDefaultWorkflow}
      />
    </div>
  )
}

export default WorkflowTable
